/* eslint-disable @stylistic/ts/indent */
import { ChangeEvent, useCallback, useMemo } from "react";
import { TextInput } from "@mantine/core";
import { get } from "lodash";
import { ReviewDestination, ReviewDestinationConfigField, ReviewDestinationInput } from "src/graphql/generated/graphql";

const sanitizeUrl = (value: string) => {
    return value.replace(/\/$/, "");
};

export interface ConfigEditorProps {
    reviewDestination: ReviewDestination;
    value: ReviewDestinationInput;
    onConfigChange(config: string);
}

export const ConfigEditor = ({ reviewDestination, value, onConfigChange }: ConfigEditorProps) => {
    const config = useMemo(() => {
        if (/^\{.*\}$/.test(value?.config)) {
            return JSON.parse(value.config);
        }

        return null;
    }, [value?.config]);

    const handleTextOrUrlChange = useCallback((field: ReviewDestinationConfigField, event: ChangeEvent<HTMLInputElement>) => {
        if (field.type !== "url") {
            onConfigChange?.(JSON.stringify({
                ...(config || {}),
                [field.path]: event?.target?.value,
            }));
            return;
        }

        if (
            !field?.urlStripQueryString
            || !/^http(s)\:\/\/.+$/.test(event?.target?.value)
        ) {
            onConfigChange?.(JSON.stringify({
                ...(config || {}),
                [field.path]: sanitizeUrl(event?.target?.value),
            }));
            return;
        }

        const productUrl = new URL(event?.target?.value);
        productUrl.search = "";
        productUrl.hash = "";

        onConfigChange?.(JSON.stringify({
            ...(config || {}),
            [field.path]: sanitizeUrl(productUrl.toString()),
        }));
    }, [onConfigChange, config]);

    return (
        <>
            {(reviewDestination?.configFields || []).map((field, index) => {
                switch (field?.type) {
                    case "text":
                    case "url":
                        return (
                            <TextInput
                                key={`${index}${field?.path}`}
                                mt={index > 0 ? "xs" : undefined}
                                label={field?.label}
                                placeholder={field?.placeholder || field?.label}
                                value={get(config, field?.path)}
                                onChange={event =>
                                    handleTextOrUrlChange(field, event)}
                            />
                        );
                }

                return null;
            })}
        </>
    );
};
